// hooks/market/useFavorites.ts
//
// Gère les produits favoris (enregistrés) de l'utilisateur connecté.
// Deux usages distincts :
//   - isFavorite(productId) + toggleFavorite(productId) : utilisés sur
//     n'importe quelle carte produit (index.tsx, DetailProduit.tsx) pour
//     le bouton cœur, avec mise à jour optimiste + rollback.
//   - favorites (liste complète avec détails produit) + loadMore/refresh :
//     utilisés par la section "Favoris" de reglages.tsx.
//
// Appelle le vrai backend :
//   POST {API_BASE_URL}/market   action: 'get-favorites' (liste paginée, détails complets)
//   POST {API_BASE_URL}/market   action: 'toggle-favorite', { product_id, favorited }
// (voir backend/handleMarket.ts → getFavorites / toggleFavorite)

import { useState, useEffect, useCallback, useRef } from 'react';
import { API_BASE_URL } from '@/lib/api';
import { getValidToken } from '@/lib/auth';
import type { MarketFeedProduct } from '@/hooks/market/useMarketFeed';

const PAGE_SIZE = 20;

export function useFavorites() {
  // ── Set des product_id favoris connus (pour isFavorite/toggle rapide,
  // sans devoir charger la liste complète des détails à chaque fois) ──
  const [favoriteIds, setFavoriteIds] = useState<Set<string>>(new Set());
  const [togglingId, setTogglingId] = useState<string | null>(null);

  // ── Liste complète avec détails produit, pour l'écran Réglages ──────
  const [favorites, setFavorites] = useState<MarketFeedProduct[]>([]);
  const [loading, setLoading]         = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError]     = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);

  const offsetRef  = useRef(0);
  const loadingRef = useRef(false);

  const fetchFavorites = useCallback(async (mode: 'initial' | 'more') => {
    if (loadingRef.current) return;
    loadingRef.current = true;

    if (mode === 'initial') setLoading(true);
    if (mode === 'more')    setLoadingMore(true);
    setError(null);

    try {
      const token = await getValidToken();
      if (!token) {
        setError('session_expired');
        return;
      }

      const currentOffset = mode === 'more' ? offsetRef.current : 0;

      const res = await fetch(`${API_BASE_URL}/market`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action:       'get-favorites',
          access_token: token,
          limit:        PAGE_SIZE,
          offset:       currentOffset,
        }),
      });

      const data = await res.json();

      if (!res.ok || !data.success) {
        setError(data.error || 'Impossible de charger les favoris');
        return;
      }

      const newFavorites: MarketFeedProduct[] = data.products || [];

      if (mode === 'more') {
        setFavorites(prev => {
          const existingIds = new Set(prev.map(p => p.product_id));
          return [...prev, ...newFavorites.filter(p => !existingIds.has(p.product_id))];
        });
      } else {
        setFavorites(newFavorites);
        // Synchronise le Set à partir de la première page — pour les
        // pages suivantes, favoriteIds ne fait que grossir via toggle.
        setFavoriteIds(new Set(newFavorites.map(p => p.product_id)));
      }

      offsetRef.current = data.next_offset ?? (currentOffset + newFavorites.length);
      setHasMore(Boolean(data.has_more));
    } catch (err) {
      console.error('[useFavorites]', err);
      setError('network_error');
    } finally {
      setLoading(false);
      setLoadingMore(false);
      loadingRef.current = false;
    }
  }, []);

  useEffect(() => {
    fetchFavorites('initial');
  }, [fetchFavorites]);

  const loadMore = useCallback(() => {
    if (!hasMore || loadingRef.current) return;
    fetchFavorites('more');
  }, [hasMore, fetchFavorites]);

  const refresh = useCallback(() => {
    offsetRef.current = 0;
    fetchFavorites('initial');
  }, [fetchFavorites]);

  const isFavorite = useCallback(
    (productId: string) => favoriteIds.has(productId),
    [favoriteIds]
  );

  const toggleFavorite = useCallback(async (productId: string) => {
    const currentlyFavorite = favoriteIds.has(productId);
    const nextFavorite = !currentlyFavorite;

    // Mise à jour optimiste sur le Set (réaction immédiate du bouton
    // cœur). La liste `favorites` avec détails complets n'est PAS
    // modifiée ici de façon optimiste — un refresh() la resynchronisera
    // si l'utilisateur consulte l'écran Réglages ensuite.
    setFavoriteIds(prev => {
      const next = new Set(prev);
      if (nextFavorite) next.add(productId);
      else next.delete(productId);
      return next;
    });
    setTogglingId(productId);

    try {
      const token = await getValidToken();
      if (!token) {
        setFavoriteIds(prev => {
          const next = new Set(prev);
          if (nextFavorite) next.delete(productId);
          else next.add(productId);
          return next;
        });
        return;
      }

      const res = await fetch(`${API_BASE_URL}/market`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action:       'toggle-favorite',
          access_token: token,
          product_id:   productId,
          favorited:    nextFavorite,
        }),
      });

      const data = await res.json();

      if (!res.ok || !data.success) {
        // Rollback si le serveur refuse
        setFavoriteIds(prev => {
          const next = new Set(prev);
          if (nextFavorite) next.delete(productId);
          else next.add(productId);
          return next;
        });
      }
    } catch (err) {
      console.error('[useFavorites][toggle]', err);
      setFavoriteIds(prev => {
        const next = new Set(prev);
        if (nextFavorite) next.delete(productId);
        else next.add(productId);
        return next;
      });
    } finally {
      setTogglingId(null);
    }
  }, [favoriteIds]);

  return {
    // Pour les cartes produit (bouton cœur)
    isFavorite,
    toggleFavorite,
    togglingId,
    // Pour l'écran Réglages (liste complète)
    favorites,
    loading,
    loadingMore,
    error,
    hasMore,
    loadMore,
    refresh,
  };
}
